import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { X, Loader2, Calendar, Clock, CheckCircle, ChevronLeft, ChevronRight, Building2, User, Mail, Phone, FileText, Globe, Briefcase, Link2, Check } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const BookDemoModal = ({ isOpen, onClose, isDark, preselectedPlan = null, showShareLink = true }) => {
  const [step, setStep] = useState(1); // 1=date, 2=form
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", notes: "", timezone: "", industry: "" });

  // Detect user timezone on mount
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) setForm(f => ({ ...f, timezone: tz }));
    } catch {}
  }, []);

  // Generate calendar days for current month view
  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = new Date(today);
    maxDate.setDate(maxDate.getDate() + 60);

    const days = [];
    for (let i = 0; i < firstDay; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isPast = date < today;
      const isTooFar = date > maxDate;
      days.push({
        day: d,
        date: date,
        dateStr: `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`,
        disabled: isWeekend || isPast || isTooFar,
      });
    }
    return days;
  }, [calendarMonth]);

  const monthLabel = calendarMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  // Fetch slots when date is selected
  useEffect(() => {
    if (!selectedDate) return;
    setLoadingSlots(true);
    setSelectedTime(null);
    axios.get(`${API}/demo/available-slots?date=${selectedDate}`)
      .then(res => setSlots(res.data.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoadingSlots(false));
  }, [selectedDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;
    if (!form.name || !form.email || !form.company || !form.timezone || !form.industry) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/demo/book`, {
        ...form,
        preferred_date: selectedDate,
        preferred_time: selectedTime,
        interested_plan: preselectedPlan?.name || null,
        interested_plan_id: preselectedPlan?.plan_id || null,
      });
      setSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to book demo. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setSelectedDate(null);
    setSelectedTime(null);
    setSlots([]);
    setSuccess(false);
    setForm({ name: "", email: "", phone: "", company: "", notes: "", timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "", industry: "" });
    onClose();
  };

  const copyShareLink = () => {
    const link = `${window.location.origin}/book-demo`;
    const done = () => {
      setLinkCopied(true);
      toast.success("Demo booking link copied — share it anywhere");
      setTimeout(() => setLinkCopied(false), 2500);
    };
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(link).then(done).catch(() => toast.info(link));
    } else {
      toast.info(link);
    }
  };

  if (!isOpen) return null;

  const accent = isDark ? "#FFCC00" : "#0046BE";
  const accentClass = isDark ? "text-[#FFCC00]" : "text-[#0046BE]";
  const accentBg = isDark ? "bg-[#FFCC00]" : "bg-[#0046BE]";
  const accentBgLight = isDark ? "bg-[#FFCC00]/10" : "bg-[#0046BE]/10";

  // The landing pages toggle dark mode via the isDark prop only (no .dark
  // class on <html>), so semantic tokens like text-foreground resolve to the
  // LIGHT palette. Use explicit colors so text is always readable.
  const tText = isDark ? "text-white" : "text-gray-900";
  const tMuted = isDark ? "text-white/60" : "text-gray-500";
  const tFaint = isDark ? "text-white/20" : "text-gray-300";
  const hoverBg = isDark ? "hover:bg-white/10" : "hover:bg-gray-100";
  const inputCls = `w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none ${isDark ? "bg-white/[0.05] border-white/10 text-white placeholder-white/30 focus:border-[#FFCC00]/50" : "bg-white border-gray-200 text-gray-900 focus:border-[#0046BE]/50"}`;
  const selectCls = `w-full px-3 py-2.5 rounded-lg border text-sm focus:outline-none ${isDark ? "bg-[#15151c] border-white/10 text-white focus:border-[#FFCC00]/50" : "bg-white border-gray-200 text-gray-900 focus:border-[#0046BE]/50"}`;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="book-demo-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className={`relative w-full max-w-lg rounded-2xl border shadow-2xl overflow-hidden max-h-[90vh] flex flex-col ${isDark ? "bg-[#0a0a0f] border-white/10" : "bg-white border-gray-200"}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b shrink-0 ${isDark ? "border-white/10" : "border-gray-200"}`}>
          <div>
            <h2 className={`text-lg font-bold ${tText}`}>Book a Demo</h2>
            <p className={`text-xs mt-0.5 ${tMuted}`}>
              {success ? "You're all set!" : step === 1 ? "Pick a date & time" : "Tell us about your business"}
            </p>
          </div>
          <div className="flex items-center gap-1">
            {showShareLink && (
              <button
                onClick={copyShareLink}
                className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors ${isDark ? "text-white/60 hover:text-white hover:bg-white/10" : "text-gray-500 hover:text-gray-900 hover:bg-gray-100"}`}
                title="Copy a shareable booking link"
                data-testid="demo-copy-link-btn"
              >
                {linkCopied ? <Check className="w-3.5 h-3.5" style={{ color: accent }} /> : <Link2 className="w-3.5 h-3.5" />}
                {linkCopied ? "Copied" : "Share link"}
              </button>
            )}
            <button onClick={handleClose} className={`p-2 rounded-lg transition-colors ${tMuted} ${hoverBg} ${isDark ? "hover:text-white" : "hover:text-gray-900"}`} data-testid="close-demo-modal">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6">
          {preselectedPlan && !success && (
            <div
              className={`mb-5 flex items-center gap-2 p-3 rounded-lg border text-sm ${isDark ? "bg-[#FFCC00]/5 border-[#FFCC00]/20" : "bg-[#0046BE]/5 border-[#0046BE]/20"}`}
              data-testid="demo-interested-plan-chip"
            >
              <Briefcase className="w-4 h-4 shrink-0" style={{ color: accent }} />
              <span className={`text-xs ${tMuted}`}>Interested in:</span>
              <span className={`font-semibold ${tText}`}>{preselectedPlan.name}</span>
            </div>
          )}
          {success ? (
            <div className="text-center py-8 space-y-4" data-testid="demo-success">
              <div className={`w-16 h-16 rounded-full mx-auto flex items-center justify-center ${accentBgLight}`}>
                <CheckCircle className="w-8 h-8" style={{ color: accent }} />
              </div>
              <h3 className={`text-xl font-bold ${tText}`}>Demo Booked!</h3>
              <p className={`text-sm max-w-xs mx-auto ${tMuted}`}>
                Your demo is scheduled for <strong className={tText}>{selectedDate}</strong> at <strong className={tText}>{selectedTime}</strong>. We'll reach out to confirm.
              </p>
              <Button onClick={handleClose} className={`${accentBg} ${isDark ? "text-black" : "text-white"} mt-4`} data-testid="demo-done-btn">
                Done
              </Button>
            </div>
          ) : step === 1 ? (
            <div className="space-y-5">
              {/* Calendar */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1))}
                    className={`p-1.5 rounded-lg ${tMuted} ${hoverBg} ${isDark ? "hover:text-white" : "hover:text-gray-900"}`} data-testid="cal-prev">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <span className={`text-sm font-semibold ${tText}`}>{monthLabel}</span>
                  <button onClick={() => setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1))}
                    className={`p-1.5 rounded-lg ${tMuted} ${hoverBg} ${isDark ? "hover:text-white" : "hover:text-gray-900"}`} data-testid="cal-next">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center mb-1">
                  {["Su","Mo","Tu","We","Th","Fr","Sa"].map(d => (
                    <div key={d} className={`text-[10px] font-medium py-1 ${tMuted}`}>{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {calendarDays.map((day, i) => (
                    <button
                      key={i}
                      disabled={!day || day.disabled}
                      onClick={() => day && !day.disabled && setSelectedDate(day.dateStr)}
                      className={`aspect-square rounded-lg text-sm font-medium transition-all
                        ${!day ? "invisible" : ""}
                        ${day?.disabled ? `${tFaint} cursor-not-allowed` : ""}
                        ${day && !day.disabled && selectedDate === day.dateStr
                          ? `${accentBg} ${isDark ? "text-black" : "text-white"} shadow-md`
                          : day && !day.disabled
                            ? `${tText} ${hoverBg} cursor-pointer`
                            : ""
                        }`}
                      data-testid={day ? `cal-day-${day.day}` : undefined}
                    >
                      {day?.day}
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              {selectedDate && (
                <div>
                  <Label className={`text-sm font-medium flex items-center gap-1.5 mb-2 ${tText}`}>
                    <Clock className="w-3.5 h-3.5" style={{ color: accent }} /> Available Times
                  </Label>
                  {loadingSlots ? (
                    <div className="flex items-center justify-center py-6">
                      <Loader2 className={`w-5 h-5 animate-spin ${tMuted}`} />
                    </div>
                  ) : slots.length === 0 ? (
                    <p className={`text-sm py-4 text-center ${tMuted}`}>No available slots for this date. Please pick another day.</p>
                  ) : (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5 max-h-[150px] overflow-y-auto">
                      {slots.map(slot => (
                        <button
                          key={slot}
                          onClick={() => setSelectedTime(slot)}
                          className={`px-2 py-2 rounded-lg text-xs font-medium transition-all border
                            ${selectedTime === slot
                              ? `${accentBg} ${isDark ? "text-black" : "text-white"} border-transparent shadow-sm`
                              : `${tText} border-transparent ${isDark ? "bg-white/[0.05] hover:bg-white/10" : "bg-gray-100 hover:bg-gray-200"}`
                            }`}
                          data-testid={`slot-${slot.replace(/\s/g, "-")}`}
                        >
                          {slot}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="demo-form">
              <div className={`flex items-center gap-3 p-3 rounded-lg border text-sm ${isDark ? "bg-white/[0.03] border-white/10" : "bg-gray-50 border-gray-200"}`}>
                <Calendar className="w-4 h-4 shrink-0" style={{ color: accent }} />
                <span className={`font-medium ${tText}`}>{selectedDate}</span>
                <Clock className="w-4 h-4 shrink-0 ml-auto" style={{ color: accent }} />
                <span className={`font-medium ${tText}`}>{selectedTime}</span>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className={`text-xs flex items-center gap-1 ${tMuted}`}><User className="w-3 h-3" /> Full Name *</Label>
                  <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    className={inputCls} placeholder="John Smith" data-testid="demo-name" />
                </div>
                <div className="space-y-1.5">
                  <Label className={`text-xs flex items-center gap-1 ${tMuted}`}><Building2 className="w-3 h-3" /> Company *</Label>
                  <input type="text" required value={form.company} onChange={e => setForm({ ...form, company: e.target.value })}
                    className={inputCls} placeholder="ABC Services" data-testid="demo-company" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className={`text-xs flex items-center gap-1 ${tMuted}`}><Mail className="w-3 h-3" /> Email *</Label>
                <input type="email" required value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                  className={inputCls} placeholder="john@company.com" data-testid="demo-email" />
              </div>

              <div className="space-y-1.5">
                <Label className={`text-xs flex items-center gap-1 ${tMuted}`}><Phone className="w-3 h-3" /> Phone <span className="opacity-50">(optional)</span></Label>
                <input type="tel" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                  className={inputCls} placeholder="(555) 123-4567" data-testid="demo-phone" />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className={`text-xs flex items-center gap-1 ${tMuted}`}><Globe className="w-3 h-3" /> Timezone *</Label>
                  <select value={form.timezone} onChange={e => setForm({ ...form, timezone: e.target.value })} required
                    className={selectCls} data-testid="demo-timezone">
                    <option value="">Select timezone...</option>
                    <option value="America/New_York">Eastern (ET)</option>
                    <option value="America/Chicago">Central (CT)</option>
                    <option value="America/Denver">Mountain (MT)</option>
                    <option value="America/Los_Angeles">Pacific (PT)</option>
                    <option value="America/Anchorage">Alaska (AKT)</option>
                    <option value="Pacific/Honolulu">Hawaii (HT)</option>
                    <option value="America/Phoenix">Arizona (no DST)</option>
                    <option value="America/Toronto">Eastern Canada</option>
                    <option value="America/Vancouver">Pacific Canada</option>
                    <option value="Europe/London">UK (GMT/BST)</option>
                    <option value="Europe/Paris">Central Europe (CET)</option>
                    <option value="Europe/Berlin">Germany (CET)</option>
                    <option value="Asia/Dubai">Gulf (GST)</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                    <option value="Asia/Singapore">Singapore (SGT)</option>
                    <option value="Australia/Sydney">Sydney (AEST)</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label className={`text-xs flex items-center gap-1 ${tMuted}`}><Briefcase className="w-3 h-3" /> Industry *</Label>
                  <select value={form.industry} onChange={e => setForm({ ...form, industry: e.target.value })} required
                    className={selectCls} data-testid="demo-industry">
                    <option value="">Select industry...</option>
                    <option value="locksmith">Locksmith</option>
                    <option value="hvac">HVAC</option>
                    <option value="plumbing">Plumbing</option>
                    <option value="electrical">Electrical</option>
                    <option value="pest_control">Pest Control</option>
                    <option value="cleaning">Cleaning</option>
                    <option value="landscaping">Landscaping</option>
                    <option value="roofing">Roofing</option>
                    <option value="garage_door">Garage Door</option>
                    <option value="appliance_repair">Appliance Repair</option>
                    <option value="general_contractor">General Contractor</option>
                    <option value="other">Other</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className={`text-xs flex items-center gap-1 ${tMuted}`}><FileText className="w-3 h-3" /> Notes <span className="opacity-50">(optional)</span></Label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                  className={`${inputCls} resize-none`}
                  placeholder="Tell us about your business or what you'd like to see" data-testid="demo-notes" />
              </div>
            </form>
          )}
        </div>

        {/* Footer */}
        {!success && (
          <div className={`px-6 py-4 border-t shrink-0 ${isDark ? "border-white/10" : "border-gray-200"}`}>
            {step === 1 ? (
              <Button
                onClick={() => setStep(2)}
                disabled={!selectedDate || !selectedTime}
                className={`w-full ${accentBg} ${isDark ? "text-black" : "text-white"} hover:opacity-90 py-5`}
                data-testid="demo-continue-btn"
              >
                Continue <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep(1)}
                  className={`flex-shrink-0 ${isDark ? "border-white/20 text-white hover:bg-white/10 bg-transparent" : ""}`}
                  data-testid="demo-back-btn">
                  <ChevronLeft className="w-4 h-4 mr-1" /> Back
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={submitting || !form.name || !form.email || !form.company || !form.timezone || !form.industry}
                  className={`flex-1 ${accentBg} ${isDark ? "text-black" : "text-white"} hover:opacity-90 py-5`}
                  data-testid="demo-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calendar className="w-4 h-4 mr-2" />}
                  Book Demo
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
